import { useState } from 'react';
import { Button } from '../components/ui/button';
import { BookingModal } from '../components/shared/BookingModal';
import { TrendingUp, Users, Target, ArrowRight, ExternalLink } from 'lucide-react';

const caseStudies = [
    {
        slug: 'healthfirst-clinics',
        title: 'HealthFirst Multi-Specialty Clinics',
        industry: 'Healthcare',
        servicesUsed: ['GMB Optimization', 'Local SEO', 'Content Strategy'],
        challenge: 'A chain of 5 clinics struggling to compete with larger hospital brands online. Low visibility in Google Maps and minimal organic traffic despite quality services.',
        solution: 'Implemented comprehensive GMB optimization across all locations, built location-specific landing pages, and created a review generation system.',
        results: {
            traffic: '+340%',
            leads: '+180%',
            mapRankings: '#1 in 4/5 locations',
            roi: '8.2x',
        },
        testimonial: {
            quote: "Aitrixy transformed our online presence. We went from page 5 to position 1 in just 4 months. Our patient inquiries have tripled.",
            author: 'Dr. Priya Sharma',
            role: 'Founder, HealthFirst Clinics',
        },
        timeline: '4 months',
    },
    {
        slug: 'travelwings-agency',
        title: 'TravelWings Holiday Packages',
        industry: 'Travel & Tourism',
        servicesUsed: ['High-Intent SEO', 'AI Search Optimization', 'Programmatic SEO'],
        challenge: 'Competing against OTAs and large travel brands for destination keywords. High competition, low brand recognition, and seasonal traffic fluctuations.',
        solution: 'Built 200+ destination landing pages using programmatic SEO, optimized for AI search platforms, and targeted high-intent "best packages" keywords.',
        results: {
            traffic: '+520%',
            leads: '+290%',
            keywords: '150+ page 1 rankings',
            roi: '12x',
        },
        testimonial: {
            quote: "The ROI has been incredible. Our GMB listing now generates 80% of our leads. Best investment we've made in marketing.",
            author: 'Rahul Mehta',
            role: 'CEO, TravelWings',
        },
        timeline: '6 months',
    },
    {
        slug: 'visapro-consultancy',
        title: 'VisaPro Immigration Consultancy',
        industry: 'Visa & Immigration',
        servicesUsed: ['GEO SEO', 'GMB Optimization', 'Content Marketing'],
        challenge: 'Needed to build trust online and capture visa seekers across multiple cities. High competition from established players and trust issues in the industry.',
        solution: 'Created city-specific landing pages for 15 major cities, built authoritative content hub, and optimized GMB for local trust signals.',
        results: {
            traffic: '+280%',
            leads: '+150%',
            cities: '15 cities ranked',
            roi: '6.5x',
        },
        testimonial: {
            quote: "Finally, an agency that delivers results, not just reports. They understood our industry and delivered exactly what they promised.",
            author: 'Anita Gupta',
            role: 'Marketing Head, VisaPro',
        },
        timeline: '5 months',
    },
    {
        slug: 'fitzone-gyms',
        title: 'FitZone Fitness Centers',
        industry: 'Fitness & Wellness',
        servicesUsed: ['GMB Optimization', 'Local SEO', 'Review Management'],
        challenge: '8-location gym chain with inconsistent online presence. Each location had different GMB quality, and they were losing members to competitors with better online visibility.',
        solution: 'Standardized GMB profiles across all locations, implemented review generation campaigns, and built location landing pages with class schedules.',
        results: {
            traffic: '+200%',
            memberships: '+85%',
            reviews: '500+ new 5-star reviews',
            roi: '5x',
        },
        testimonial: {
            quote: "Our membership inquiries have doubled since working with Aitrixy. The review management alone was worth the investment.",
            author: 'Karan Malhotra',
            role: 'Operations Director, FitZone',
        },
        timeline: '3 months',
    },
];

export default function CaseStudiesPage() {
    const [bookingOpen, setBookingOpen] = useState(false);

    return (
        <div className="relative" data-testid="case-studies-page">
            {/* Hero */}
            <section className="section-padding bg-muted/30">
                <div className="container-custom">
                    <div className="max-w-4xl">
                        <p className="text-sm font-medium tracking-wider uppercase text-primary mb-4">
                            Case Studies
                        </p>
                        <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
                            Real Results, Real Growth
                        </h1>
                        <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                            See how we've helped businesses across industries achieve measurable 
                            growth in search visibility, leads, and revenue.
                        </p>
                    </div>
                </div>
            </section>

            {/* Case Studies List */}
            <section className="section-padding">
                <div className="container-custom">
                    <div className="space-y-24">
                        {caseStudies.map((study, index) => (
                            <div 
                                key={index}
                                className="scroll-mt-24"
                                data-testid={`case-study-${index}`}
                            >
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                                    {/* Left - Details */}
                                    <div>
                                        <div className="flex items-center gap-3 mb-4">
                                            <span className="px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary">
                                                {study.industry}
                                            </span>
                                            <span className="text-sm text-muted-foreground">
                                                {study.timeline}
                                            </span>
                                        </div>

                                        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
                                            {study.title}
                                        </h2>

                                        <div className="space-y-6 mb-8">
                                            <div>
                                                <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-2">
                                                    Challenge
                                                </h4>
                                                <p className="text-muted-foreground">{study.challenge}</p>
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-2">
                                                    Solution
                                                </h4>
                                                <p className="text-muted-foreground">{study.solution}</p>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-2 mb-8">
                                            {study.servicesUsed.map((service, i) => (
                                                <span 
                                                    key={i}
                                                    className="px-3 py-1 rounded-full text-sm bg-muted text-muted-foreground"
                                                >
                                                    {service}
                                                </span>
                                            ))}
                                        </div>

                                        {/* Testimonial */}
                                        <div className="p-6 rounded-2xl bg-muted/50 border border-border">
                                            <p className="text-lg italic mb-4">"{study.testimonial.quote}"</p>
                                            <div>
                                                <div className="font-semibold">{study.testimonial.author}</div>
                                                <div className="text-sm text-muted-foreground">{study.testimonial.role}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right - Results */}
                                    <div className="lg:sticky lg:top-24">
                                        <div className="rounded-3xl bg-foreground text-background p-8 md:p-10">
                                            <h3 className="text-xl font-semibold mb-8">Results Achieved</h3>
                                            
                                            <div className="grid grid-cols-2 gap-6 mb-8">
                                                <div>
                                                    <div className="flex items-center gap-2 text-background/60 text-sm mb-2">
                                                        <TrendingUp className="w-4 h-4" />
                                                        Traffic Growth
                                                    </div>
                                                    <div className="text-3xl font-bold text-green-400">
                                                        {study.results.traffic}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2 text-background/60 text-sm mb-2">
                                                        <Users className="w-4 h-4" />
                                                        Lead Increase
                                                    </div>
                                                    <div className="text-3xl font-bold text-green-400">
                                                        {study.results.leads}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2 text-background/60 text-sm mb-2">
                                                        <Target className="w-4 h-4" />
                                                        ROI
                                                    </div>
                                                    <div className="text-3xl font-bold text-green-400">
                                                        {study.results.roi}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2 text-background/60 text-sm mb-2">
                                                        <ExternalLink className="w-4 h-4" />
                                                        Key Metric
                                                    </div>
                                                    <div className="text-lg font-semibold text-background">
                                                        {study.results.mapRankings || study.results.keywords || study.results.reviews || study.results.memberships}
                                                    </div>
                                                </div>
                                            </div>

                                            <Button 
                                                className="w-full bg-background text-foreground hover:bg-background/90 rounded-full"
                                                onClick={() => setBookingOpen(true)}
                                            >
                                                Get Similar Results
                                                <ArrowRight className="w-4 h-4 ml-2" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>

                                {index < caseStudies.length - 1 && (
                                    <div className="border-b border-border mt-24" />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="section-padding bg-muted/30">
                <div className="container-custom text-center">
                    <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
                        Want Results Like These?
                    </h2>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                        Every business is unique. Let's discuss how we can create a custom 
                        strategy that delivers measurable growth for your business.
                    </p>
                    <Button 
                        size="lg"
                        className="btn-glow rounded-full px-8"
                        onClick={() => setBookingOpen(true)}
                        data-testid="case-studies-cta-btn"
                    >
                        Book Free Strategy Call
                        <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                </div>
            </section>

            <BookingModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
        </div>
    );
}
